import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink,
  MessageCircle, Instagram, Search, Send, Smile, Paperclip, Phone, Video,
  MoreVertical, CheckCheck, ShieldCheck, FlaskConical, Trash2, Loader2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useClinic } from "@/hooks/useClinic";
import { ManualControl, ManualBanner } from "@/components/ManualControl";
import { getConversations, sendChat, deleteConversation, clearConversations } from "@/lib/dentiumApi";

const CH = {
  whatsapp: {
    id: "whatsapp", name: "WhatsApp", icon: MessageCircle, color: "#25D366",
    panelHeader: "#F0F2F5", chatArea: "wa-pattern",
    incoming: { bg: "#FFFFFF", color: "#111827" },
    outgoing: { bg: "#D9FDD3", color: "#111827" },
    radius: "rounded-lg", accent: "#25D366",
  },
  instagram: {
    id: "instagram", name: "Instagram", icon: Instagram, color: "#E1306C",
    panelHeader: "#FFFFFF", chatArea: "bg-white",
    incoming: { bg: "#EFEFEF", color: "#262626" },
    outgoing: { bg: "#3797F0", color: "#FFFFFF" },
    radius: "rounded-[20px]", accent: "#3797F0",
  },
};

let counter = 0;
const newId = () => `${Date.now()}-${counter++}`;
const nowTime = () => new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });


const initials = (name = "") =>
  name.replace(/[@]/g, "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "D";

const timeShort = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
};

export const ChatWorkspace = ({ externalChannel, fullHeight = false }) => {
  const qc = useQueryClient();
  const [channel, setChannel] = useState(externalChannel || "whatsapp");
  const [activeId, setActiveId] = useState("live");
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");
  const [consent, setConsent] = useState(false);
  const [typing, setTyping] = useState(false);
  const [liveSessions] = useState(() => ({ whatsapp: `demo-whatsapp-${newId()}`, instagram: `demo-instagram-${newId()}` }));
  const [liveThreads, setLiveThreads] = useState({ whatsapp: [], instagram: [] });
  const clinic = useClinic();
  const viewportRef = useRef(null);

  const c = CH[channel];

  useEffect(() => { if (externalChannel && CH[externalChannel]) { setChannel(externalChannel); setActiveId("live"); } }, [externalChannel]);

  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations", channel, "todos"],
    queryFn: () => getConversations(channel, "todos"),
    refetchInterval: 5000,
  });

  const delOne = useMutation({
    mutationFn: deleteConversation,
    onSuccess: (_d, id) => {
      if (activeId === id) setActiveId("live");
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Conversación eliminada");
    },
    onError: () => toast.error("No se pudo eliminar la conversación"),
  });

  const clearAll = useMutation({
    mutationFn: clearConversations,
    onSuccess: (d) => {
      setActiveId("live");
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Conversaciones limpiadas", { description: `${d.deleted ?? 0} conversaciones eliminadas.` });
    },
    onError: () => toast.error("No se pudieron limpiar las conversaciones"),
  });

  const handleDeleteOne = (e, id) => {
    e.stopPropagation();
    if (window.confirm("¿Eliminar esta conversación? Esta acción no se puede deshacer.")) delOne.mutate(id);
  };

  const handleClearAll = () => {
    if (window.confirm("¿Eliminar TODAS las conversaciones del historial (ambos canales)? El chat de prueba no se ve afectado. Esta acción no se puede deshacer.")) {
      clearAll.mutate();
    }
  };

  const liveConvo = {
    id: "live", contact_name: "Chat de prueba", last_message: liveThreads[channel][liveThreads[channel].length - 1]?.text || "",
    updated_at: null, isLive: true,
  };
  const list = [liveConvo, ...conversations].filter((cv) =>
    cv.isLive ||
    (cv.contact_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (cv.last_message || "").toLowerCase().includes(search.toLowerCase())
  );

  const active = activeId === "live" ? liveConvo : conversations.find((cv) => cv.id === activeId) || liveConvo;
  const isLive = active.id === "live";
  const messages = isLive ? liveThreads[channel] : (active.messages || []);

  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
  }, [messages, typing, activeId, channel]);

  const push = (msg) => setLiveThreads((t) => ({ ...t, [channel]: [...t[channel], { ...msg, id: newId() }] }));

  const handleSend = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || typing || !isLive) return;
    setInput("");
    push({ role: "user", kind: "text", text, time: nowTime() });
    setTyping(true);
    try {
      const res = await sendChat({ session_id: liveSessions[channel], channel, message: text, schedule_mode: "auto", consent });
      setTyping(false);
      if (res.bot_paused) {
        toast.info("Bot pausado", { description: "El mensaje se ha guardado, pero el bot no responde automáticamente." });
        qc.invalidateQueries({ queryKey: ["conversations"] });
        return;
      }
      if (res.lead_saved) toast.success("Contacto recogido", { description: "Nuevo lead guardado en el CRM (cifrado)." });
      push({ role: "bot", kind: "text", text: res.reply, button: res.button, time: nowTime() });
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch {
      setTyping(false);
      push({ role: "bot", kind: "text", text: "Disculpa, no hemos podido procesar tu mensaje. Inténtalo de nuevo.", time: nowTime() });
    }
  };

  return (
    <div id="chat-panel" data-testid="chat-workspace" className={fullHeight ? "flex flex-col h-full bg-white" : "rounded-2xl border border-[#E7E2DA] bg-white overflow-hidden"}>
      {/* Top bar: channel switch */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#EDE7DE] bg-white flex-wrap">
        <div>
          <h2 className="font-display text-xl font-normal text-[#1A1A1A]">Panel de conversaciones</h2>
          <p className="text-xs text-[#8A7E70]">Estilo {c.name} de escritorio · bandeja y chat en vivo</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="clear-conversations-btn"
            onClick={handleClearAll}
            disabled={clearAll.isPending || conversations.length === 0}
            className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 px-3.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {clearAll.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Limpiar conversaciones
          </button>
          <div className="flex items-center gap-1 bg-[#F1EBE1] rounded-full p-1">
            {Object.values(CH).map((ch) => {
              const Icon = ch.icon;
              const on = channel === ch.id;
              return (
                <button
                  key={ch.id}
                  data-testid={`channel-tab-${ch.id}`}
                  onClick={() => { setChannel(ch.id); setActiveId("live"); }}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${on ? "bg-white text-[#1A1A1A] shadow-sm" : "text-[#8A7E70]"}`}
                >
                  <Icon className="h-4 w-4" style={{ color: on ? ch.color : undefined }} /> {ch.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Two-pane desktop messenger */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0" style={fullHeight ? undefined : { height: "78vh", minHeight: 560 }}>
        {/* LEFT: conversation list */}
        <aside className="w-full md:w-[340px] shrink-0 border-b md:border-b-0 md:border-r border-[#EDE7DE] flex flex-col" style={{ background: "#FFFFFF" }}>
          <div className="px-3 py-2.5" style={{ background: c.panelHeader }}>
            <div className="flex items-center gap-2 rounded-lg bg-white border border-[#E7E2DA] px-3 py-1.5">
              <Search className="h-4 w-4 text-[#A79E90]" />
              <input
                data-testid="conversation-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar o empezar un chat"
                className="flex-1 bg-transparent text-sm outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto dentium-scroll max-h-[240px] md:max-h-none">
            {list.map((cv) => {
              const on = activeId === cv.id;
              return (
                <div
                  key={cv.id}
                  className={`group relative flex items-center border-b border-[#F3EEE6] transition-colors ${on ? "bg-[#F1EBE1]" : "hover:bg-[#FAF8F5]"}`}
                >
                  <button
                    data-testid={cv.isLive ? "conversation-item-live" : "conversation-item"}
                    onClick={() => setActiveId(cv.id)}
                    className="flex-1 min-w-0 text-left flex items-center gap-3 px-3 py-3"
                  >
                    <span
                      className="h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold shrink-0 text-sm"
                      style={{ background: cv.isLive ? "#1A1A1A" : c.color }}
                    >
                      {cv.isLive ? <FlaskConical className="h-5 w-5" /> : initials(cv.contact_name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-[#1A1A1A] truncate">{cv.contact_name || "Contacto"}</span>
                        <span className="text-[10px] text-[#A79E90] shrink-0">{cv.isLive ? "en vivo" : timeShort(cv.updated_at)}</span>
                      </div>
                      <div className="text-xs text-[#8A7E70] truncate mt-0.5">{cv.last_message || "Nueva conversación de prueba"}</div>
                    </div>
                  </button>
                  {!cv.isLive && (
                    <button
                      data-testid="delete-conversation-btn"
                      onClick={(e) => handleDeleteOne(e, cv.id)}
                      disabled={delOne.isPending}
                      title="Eliminar conversación"
                      className="shrink-0 mr-2 h-8 w-8 rounded-full flex items-center justify-center text-[#A79E90] hover:bg-red-100 hover:text-red-600 transition-colors md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* RIGHT: active chat */}
        <section className="flex-1 flex flex-col min-w-0">
          {/* chat header */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#EDE7DE]" style={{ background: c.panelHeader }}>
            <span className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0" style={{ background: isLive ? "#1A1A1A" : c.color }}>
              {isLive ? <FlaskConical className="h-4.5 w-4.5" /> : initials(active.contact_name)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#111827] truncate">{active.contact_name || "Contacto"}</div>
              <div className="text-[11px] text-[#6B7280]">{isLive ? "chat de prueba · escribes como cliente" : active.manual_mode ? "atendida manualmente por el equipo" : "respuestas automáticas del bot"}</div>
            </div>
            <div className="flex items-center gap-3 text-[#54656F]">
              <Phone className="h-4.5 w-4.5" /><Video className="h-5 w-5" /><MoreVertical className="h-5 w-5" />
            </div>
          </div>

          {!isLive && active.manual_mode && <ManualBanner conv={active} />}

          {/* messages */}
          <div ref={viewportRef} data-testid="chat-messages-viewport" className={`flex-1 overflow-y-auto dentium-scroll px-4 sm:px-8 py-4 space-y-2 ${c.chatArea}`}>
            {isLive && messages.length === 0 && (
              <div data-testid="live-chat-empty" className="flex justify-center my-6">
                <div className="text-xs text-center max-w-[80%] rounded-lg px-3 py-2 bg-white/80 text-[#6B6157] shadow-sm">
                  Escribe un mensaje como si fueras un paciente: el bot responderá con el mensaje de bienvenida configurado.
                </div>
              </div>
            )}
            {messages.map((m) => {
              if (m.kind === "privacy") {
                return (
                  <div key={m.id || m.time + m.text} className="flex justify-center my-2">
                    <div className="text-[11px] text-center max-w-[80%] rounded-lg px-3 py-1.5 bg-black/5 text-[#6B6157]">{m.text}</div>
                  </div>
                );
              }
              const isAgent = m.role === "agent";
              const isBot = m.role === "bot" || isAgent; // clínica → derecha
              const style = isBot ? c.outgoing : c.incoming;
              const senderName = isAgent ? `${m.by || "Equipo"} · equipo` : isBot ? (clinic.name || "Clínica") : (active.contact_name && !isLive ? active.contact_name : "Cliente");
              return (
                <div key={m.id || m.time + m.text} className={`flex ${isBot ? "justify-end" : "justify-start"}`}>
                  <div className={`bubble-in max-w-[68%] px-3 py-2 text-sm leading-snug shadow-sm ${c.radius}`} style={{ background: style.bg, color: style.color }}>
                    <div className="text-[10px] font-semibold mb-0.5 opacity-70">{senderName}</div>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    {m.button?.url && (
                      <a data-testid="chat-message-link-button" href={m.button.url} target="_blank" rel="noopener noreferrer"
                        className="mt-2 -mx-1 flex items-center justify-center gap-1.5 rounded-lg border border-black/10 bg-white/70 hover:bg-white px-3 py-2 text-sm font-semibold text-[#0B7AD1] transition-colors">
                        <ExternalLink className="h-3.5 w-3.5" /> {m.button.label}
                      </a>
                    )}
                    <div className="flex items-center justify-end gap-1 mt-0.5">
                      {isAgent && m.delivered === false && <span data-testid="manual-msg-not-delivered" className="text-[10px] opacity-70 mr-1">No entregado al canal</span>}
                      <span className="text-[10px] opacity-60">{m.time}</span>
                      {isBot && channel === "whatsapp" && <CheckCheck className="h-3 w-3" style={{ color: "#53BDEB" }} />}
                    </div>
                  </div>
                </div>
              );
            })}
            {typing && isLive && (
              <div className="flex justify-end">
                <div className={`px-3 py-2.5 shadow-sm ${c.radius}`} style={{ background: c.outgoing.bg }}>
                  <div className="flex gap-1">
                    <span className="typing-dot h-1.5 w-1.5 rounded-full bg-black/40" />
                    <span className="typing-dot h-1.5 w-1.5 rounded-full bg-black/40" style={{ animationDelay: "0.2s" }} />
                    <span className="typing-dot h-1.5 w-1.5 rounded-full bg-black/40" style={{ animationDelay: "0.4s" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* footer: input (solo chat en vivo) */}
          {isLive ? (
            <div className="border-t border-[#EDE7DE] bg-white">
              <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: channel === "whatsapp" ? "#F0F2F5" : "#FFFFFF" }}>
                <Smile className="h-5 w-5 text-[#54656F] shrink-0" />
                <Paperclip className="h-5 w-5 text-[#54656F] shrink-0" />
                <input
                  data-testid="chat-input-text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder="Escribe un mensaje como cliente…"
                  className="flex-1 bg-white border border-[#E7E2DA] rounded-full px-4 py-2 text-sm outline-none"
                />
                <button data-testid="chat-send-btn" onClick={() => handleSend()} className="h-10 w-10 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: c.accent }}>
                  <Send className="h-4.5 w-4.5" />
                </button>
              </div>
              <div className="px-3 pb-3 flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  {["¿La primera visita es gratis?", "Quiero pedir una cita", "Me duele una muela"].map((p, i) => (
                    <button
                      key={i}
                      data-testid={`quick-prompt-${i}`}
                      onClick={() => handleSend(p)}
                      disabled={typing}
                      className="text-xs font-medium text-[#1A1A1A] bg-[#F1EBE1] hover:bg-[#1A1A1A] hover:text-white rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
                    >{p}</button>
                  ))}
                </div>
                <label className="flex items-start gap-2 text-[11px] text-[#8A7E70] cursor-pointer">
                  <Checkbox data-testid="chat-rgpd-consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 data-[state=checked]:bg-[#1A1A1A] data-[state=checked]:border-[#1A1A1A]" />
                  <span><ShieldCheck className="inline h-3.5 w-3.5 text-[#1A1A1A] mr-1 -mt-0.5" />Acepto el tratamiento de mis datos (RGPD) para gestionar la cita. Necesario para recoger el contacto.</span>
                </label>
              </div>
            </div>
          ) : (
            <ManualControl conv={active} accent={c.accent} />
          )}
        </section>
      </div>
    </div>
  );
};
